import axios, { AxiosError } from 'axios';

/**
 * Comment Service
 *
 * Handles all comment-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

/**
 * Comment interfaces
 */
export interface RequirementComment {
  id: string;
  requirement_id: string;
  parent_comment_id: string | null;
  user_id: string;
  comment_text: string;
  is_edited: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string | null;
  user_name: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  replies?: RequirementComment[];
}

export interface CreateCommentInput {
  comment_text: string;
  parent_comment_id?: string;
}

export interface UpdateCommentInput {
  comment_text: string;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Get all comments for a requirement (threaded structure)
 */
export async function getComments(
  projectId: string,
  requirementId: string,
  token: string
): Promise<RequirementComment[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/comments`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data.data;
  } catch (error) {
    return handleAPIError(error);
  }
}

/**
 * Create a new comment
 */
export async function createComment(
  projectId: string,
  requirementId: string,
  input: CreateCommentInput,
  token: string
): Promise<RequirementComment> {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/comments`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data.data;
  } catch (error) {
    return handleAPIError(error);
  }
}

/**
 * Update a comment
 */
export async function updateComment(
  projectId: string,
  requirementId: string,
  commentId: string,
  input: UpdateCommentInput,
  token: string
): Promise<RequirementComment> {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/comments/${commentId}`,
      input,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data.data;
  } catch (error) {
    return handleAPIError(error);
  }
}

/**
 * Delete a comment (soft delete)
 */
export async function deleteComment(
  projectId: string,
  requirementId: string,
  commentId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete(
      `${API_BASE_URL}/projects/${projectId}/requirements/${requirementId}/comments/${commentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  } catch (error) {
    return handleAPIError(error);
  }
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Get user initials for avatar
 */
export function getUserInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}
